import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faPeopleGroup, faBell, faRobot, faSave, faPlus, faChevronCircleLeft, faEye, faEdit } from '@fortawesome/free-solid-svg-icons';
import { faBuilding, faCircleDown } from '@fortawesome/free-regular-svg-icons';
import { get } from '../utils/apiUtils';
import { getAllEntitiesByFilter, getEntityById, updateEntity, createEntity } from '../services/entityService_V2';
import { useToast } from '../components/common/ToastProvider';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Input,
  PageContainer,
  SectionTitle,
  SubSectionTitle,
  ButtonSuccess,
  Button,
  Select,
  FormGroup,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  FormRow,
  Label,
  TableFormGroup,
  FlexLayout,
  Flex1ThirdLayout,
  Flex2ThirdLayout,
  FormRowFull,
  Toggle,
  ToggleInput,
  Divider,
  Card,
  CardTitle,
  fadeIn,
  useGoBack,
  Textarea
} from '../styles/PageStyles';


// Markdown editor container with preview toggle
const MarkdownEditorContainer = styled.div`
  position: relative;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: ${props => props.active ? 'var(--primary)' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : '#ccc'};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-dark)' : '#e9ecef'};
  }
`;

const MarkdownPreview = styled.div`
  padding: 0.75rem;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  line-height: 1.6;
  
  h1, h2, h3, h4, h5, h6 { margin: 0.5em 0; }
  p { margin: 0.5em 0; }
  ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
  code { background: #f1f3f4; padding: 2px 4px; border-radius: 3px; }
  pre { background: #f8f9fa; padding: 1rem; border-radius: 4px; overflow-x: auto; }
  blockquote { border-left: 4px solid #dee2e6; padding-left: 1rem; margin: 1rem 0; }
`;

// Custom green toggle switch component
const GreenSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 30px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 34px;
  }
  
  .slider:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  
  input:checked + .slider {
    background-color: #76b947;
  }
  
  input:focus + .slider {
    box-shadow: 0 0 1px #76b947;
  }
  
  input:checked + .slider:before {
    transform: translateX(30px);
  }
`;


const EntityDetails_V2 = ({ group, onSelectUser, onSaveGroup }) => {
  const { entityId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entityDetail, setEntityDetail] = useState();
  const [subEntityList, setSubEntityList] = useState();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const {showToast} = useToast();

  useEffect(() => {
    const fetchEntityById = async () => {
      try {
        const parsedEntityId = Number(entityId); // Parse entityId as a number
        if (isNaN(parsedEntityId)) {
          return;
        }
        const response = await getEntityById((parsedEntityId));
        setEntityDetail(response);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    if (entityId !== 'new') {
      fetchEntityById();
    } else {
      if (user) {
        DefaultEntity.company_cd = user.companyCd;
        setEntityDetail(DefaultEntity);
      }
    }
  }, [entityId, user]);

  useEffect(() => {
    const fetchSubEntityList = async () => {
      const filters = {
        "type": "sub_entity"
      };
      const sort = {};
      try {
        const response = await getAllEntitiesByFilter(filters, sort,'skip=0&limit=100');
        setSubEntityList(response || []);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchSubEntityList();
  }, [setSubEntityList]);

  const filteredAttributes = (entityDetail?.attributes || []).filter((attrib) => {
    return attrib?.name?.toLowerCase().includes(searchTerm?.toLowerCase())
    // return user
  });


  const openDrawer = (attrib) => {
    setSelectedAttribute(attrib); // Set the selected attribute
    setIsDrawerOpen(true); // Open the drawer
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false); // Close the drawer
    setSelectedAttribute(null); // Clear the selected attribute
  };

  const saveAttributeChanges = (updatedAttribute) => {
    const updatedAttributes = entityDetail.attributes.map((attrib) =>
      attrib.name === selectedAttribute.name ? updatedAttribute : attrib
    );
    setEntityDetail({ ...entityDetail, attributes: updatedAttributes });
    closeDrawer(); // Close the drawer after saving
  };
  const defaultAttributeModel = {
    name: '',
    type: 'STR',
    description: '',
    isList: false,
    entity:null
  };
  const DefaultEntity = {
    "name": '',
    "type": 'entity',
    "status": "INACTIVE",
    "attributes": [],
    "company_cd": '',
    "description": '',
  }
  const addRemoveAttribute = (index) => {
    if (typeof index === 'number') {
      // Remove the attribute at the specified index
      const updatedAttributes = (entityDetail?.attributes || []).filter((_, i) => i !== index);
      setEntityDetail({ ...entityDetail, attributes: updatedAttributes });
    } else {
      // Add a new attribute using the default model
      const updatedAttributes = [...(entityDetail?.attributes || []), { ...defaultAttributeModel }];
      setEntityDetail({ ...entityDetail, attributes: updatedAttributes });
    }
  };

  const handleTypeChange = (selectedType) => {
    let updatedEntityDetail = { ...entityDetail, type: selectedType };
    
    // If document type is selected, automatically add markdown attribute
    if (selectedType === 'document') {
      const hasMarkdownAttribute = entityDetail?.attributes?.some(attr => attr.name === 'markdown');
      if (!hasMarkdownAttribute) {
        const markdownAttribute = {
          name: 'markdown',
          type: 'STR',
          description: 'Document content in markdown format',
          isList: false,
          entity: null,
          businessKey: false,
          isMandatory: false
        };
        updatedEntityDetail = {
          ...updatedEntityDetail,
          attributes: [...(entityDetail?.attributes || []), markdownAttribute]
        };
      }
    }
    
    setEntityDetail(updatedEntityDetail);
  };

  const saveEntity = async (isPublished = false) => {
    try {
      // Validate entityDetail.name
      if (!entityDetail.name || entityDetail.name.trim() === '') {
        showToast({
          message: 'Entity name cannot be blank.',
          type: 'warning'
        })
        return;
      }

      // Validate attributes
      const updatedAttributes = (entityDetail.attributes || []).map((attrib) => {
        if (!attrib.name || attrib.name.trim() === '') {
          throw new Error('Attribute name cannot be blank.');
        }
        // Replace spaces with underscores and convert to lowercase
        return {
          ...attrib,
          name: attrib.name.replace(/\s+/g, '_'),
        };
      });
      // Prepare the updated entityDetail object
      const updatedEntityDetail = {
        ...entityDetail,
        ...(isPublished ? { status: "ACTIVE" } : {}),
        attributes: updatedAttributes
      };


      // Save or update entity

      if (updatedEntityDetail.id) {
        let tmpEntity = {...updatedEntityDetail};
        if(!tmpEntity.version) {
          tmpEntity.version = 1;
        }else if(isPublished){
          tmpEntity.version += 1;
        }
        await updateEntity(tmpEntity);
        showToast({
          message: 'Entity saved successfully',
          type:'success'
        })
        navigate('/entity');
      } else {
        let tmpEntity = {...updatedEntityDetail};
        tmpEntity.version = 1;
        tmpEntity['code'] = tmpEntity?.name;
        const response = await createEntity(tmpEntity);
         showToast({
          message: 'Entity created successfully',
          type:'success'
        })
        setEntityDetail({ ...updatedEntityDetail, id: response.id });
        await updateEntity({ ...updatedEntityDetail, id: response.id });
      }
    } catch (error) {
      console.error('Error saving entity:', error);
    }
  }

  const goBack = useGoBack();
  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <SectionTitle>
          {entityDetail ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Button onClick={goBack} secondary ><FontAwesomeIcon icon={faChevronCircleLeft} /></Button>
              <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                Edit {entityDetail.name}
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Button onClick={goBack} secondary ><FontAwesomeIcon icon={faChevronCircleLeft} /></Button>
            <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              Add Entity
            </span>
            </div>
          )}
        </SectionTitle>
        <Divider />
        <FormRow>
          <FormGroup>
            <Label>Name</Label>
            <Input type="text" value={entityDetail?.name || ''} onChange={(e) => setEntityDetail({ ...entityDetail, name: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Type</Label>
            <Select
              value={entityDetail?.type || ''}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              <option value="">Select Type</option>
              <option value="entity">Entity</option>
              <option value="sub_entity">Sub-Entity</option>
              <option value="document">Document</option>
            </Select>
          </FormGroup>
        </FormRow>  
        <FormRowFull>
          <FormGroup>
            <Label>Description</Label>
            <div style={{ marginTop: '0.5rem' }}>
              <MarkdownEditorContainer>
                <ToggleButton
                  active={isPreviewMode}
                  onClick={() => setIsPreviewMode(!isPreviewMode)}
                  type="button"
                >
                  <FontAwesomeIcon icon={isPreviewMode ? faEdit : faEye} />
                  {isPreviewMode ? 'Edit' : 'Preview'}
                </ToggleButton>
                
                {isPreviewMode ? (
                  <MarkdownPreview>
                    {entityDetail?.desc ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {entityDetail.desc}
                      </ReactMarkdown>
                    ) : (
                      <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                        No description provided
                      </p>
                    )}
                  </MarkdownPreview>
                ) : (
                  <Textarea
                    value={entityDetail?.desc || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Only update if the value actually changed to prevent duplicate requests
                      if (val !== entityDetail?.desc) {
                        setEntityDetail(prev => ({
                          ...prev,
                          desc: val || ''
                        }));
                      }
                    }}
                    placeholder="Enter description in markdown format..."
                    style={{
                      height: '200px',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      resize: 'vertical',
                      minHeight: '150px',
                      border: 'none',
                      borderRadius: '0',
                      outline: 'none'
                    }}
                  />
                )}
              </MarkdownEditorContainer>
            </div>
          </FormGroup>
        </FormRowFull>
        {entityDetail?.id && (
          <>
            <Divider />
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem' }}>
              <SubSectionTitle>Attributes</SubSectionTitle>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px', alignItems: 'center' }}>
                <Input type="text" placeholder="Search Attributes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <ButtonSuccess onClick={() => addRemoveAttribute()}><FontAwesomeIcon icon={faPlus} /> Attribute</ButtonSuccess>
              </div>
            </div>
            <Divider />
            <Table>
              <thead>
                <tr>
                  <TableHeader>Name </TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Set as Business Key</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </thead>
              <tbody>
                {filteredAttributes.map((attrib, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TableFormGroup>
                        <Input type="text" value={attrib?.name || ''} onChange={(e) => {
                          const updatedAttributes = [...entityDetail.attributes];
                          updatedAttributes[index] = { ...attrib, name: e.target.value };
                          setEntityDetail({ ...entityDetail, attributes: updatedAttributes });
                        }} />
                      </TableFormGroup>
                    </TableCell>
                    <TableCell>
                      <TableFormGroup>
                        <Select
                          value={attrib?.type || ''}
                          onChange={(e) => {
                            const updatedAttributes = [...entityDetail.attributes];
                            updatedAttributes[index] = { ...attrib, type: e.target.value };
                            setEntityDetail({ ...entityDetail, attributes: updatedAttributes });
                          }}
                        >
                          <option value="">Select Type</option>
                          <option value="STR">String</option>
                          <option value="INT">Integer</option>
                          <option value="FLOAT">Float</option>
                          <option value="DATE">Date</option>
                          <option value="BOOLEAN">Boolean</option>
                          <option value="ENTITY">Entity</option>
                          <option value="sub_entity">Sub Entity</option>
                        </Select>
                      </TableFormGroup>
                    </TableCell>
                    
                    <TableCell style={{ textAlign: 'center' }}>
                      <GreenSwitch>
                        <input 
                          type="checkbox" 
                          checked={attrib?.businessKey === true} 
                          onChange={(e) => {
                            // Create a new array where all attributes have businessKey set to false
                            const updatedAttributes = [...entityDetail.attributes].map(attr => ({
                              ...attr,
                              businessKey: false
                            }));
                            // Only set this attribute's businessKey to true if the toggle is being turned on
                            if (e.target.checked) {
                              updatedAttributes[index] = { ...attrib, businessKey: true };
                            }
                            setEntityDetail({ ...entityDetail, attributes: updatedAttributes });
                          }} 
                        />
                        <span className="slider"></span>
                      </GreenSwitch>
                    </TableCell>
                    <TableCell>

                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button onClick={() => openDrawer(attrib)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', }} > Edit </Button>
                        <Button onClick={() => addRemoveAttribute(index)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', color: 'white', backgroundColor: '#ff4444', borderColor: '#ff4444' }}>Remove</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </>
        )}

       <div style={{display:'flex', gap:'10px'}}>
           <Button primary onClick={() => saveEntity()}><FontAwesomeIcon icon={faSave} /> Save</Button>
           <Button primary onClick={() => saveEntity(true)}><FontAwesomeIcon icon={faSave} /> Publish</Button>
       </div>


      </motion.div>

      {/* Custom Drawer */}
      {isDrawerOpen && (
        <div className="custom-drawer">
          <div className="drawer-content">
            <SubSectionTitle>Edit Attribute</SubSectionTitle>
            <Divider />
            {selectedAttribute && (
              <div>
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    type="text" disabled={true}
                    value={selectedAttribute.name || ''}
                    onChange={(e) =>
                      setSelectedAttribute({
                        ...selectedAttribute,
                        name: e.target.value,
                      })
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Type</Label>
                  <Select
                    value={selectedAttribute.type || ''}
                    onChange={(e) =>
                      setSelectedAttribute({
                        ...selectedAttribute,
                        type: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Type</option>
                    <option value="STR">String</option>
                    <option value="INT">Integer</option>
                    <option value="FLOAT">Float</option>
                    <option value="DATE">Date</option>
                    <option value="BOOLEAN">Boolean</option>
                    <option value="ENTITY">Entity</option>
                    <option value="sub_entity">Sub Entity</option>
                  </Select>
                </FormGroup>
                {selectedAttribute.type === 'sub_entity' && (
                  <FormGroup>
                    <Label>Sub-Entity</Label>
                    <Select
                      value={selectedAttribute.entity_ref || ''}
                      onChange={(e) =>
                        setSelectedAttribute({
                          ...selectedAttribute,
                          entity_ref: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Entity</option>
                      {subEntityList.map((entity) => (
                        <option key={entity.name} value={entity.name}>
                          {entity.name}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                )}
                {selectedAttribute.type !== 'BOOLEAN' && (
                  <FormGroup>
                    <Label>Is List?</Label>
                    <GreenSwitch>
                      <input
                        type="checkbox"
                        checked={selectedAttribute.isList || false}
                        onChange={(e) =>
                          setSelectedAttribute({
                            ...selectedAttribute,
                            isList: e.target.checked,
                          })
                        }
                      />
                      <span className="slider"></span>
                    </GreenSwitch>
                  </FormGroup>
                )}
                <FormGroup>
                  <Label>Mandatory?</Label>
                  <GreenSwitch>
                    <input
                      type="checkbox"
                      checked={selectedAttribute.isMandatory || false}
                      onChange={(e) =>
                        setSelectedAttribute({
                          ...selectedAttribute,
                          isMandatory: e.target.checked,
                        })
                      }
                    />
                    <span className="slider"></span>
                  </GreenSwitch>
                </FormGroup>

                <FormGroup>
                  <Label>Desciption</Label>
                  <Input
                    type="text"
                    value={selectedAttribute.description || ''}
                    onChange={(e) =>
                      setSelectedAttribute({
                        ...selectedAttribute,
                        description: e.target.value,
                      })
                    }
                  />
                </FormGroup>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <ButtonSuccess style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                    onClick={() => saveAttributeChanges(selectedAttribute)}
                  >
                    Save
                  </ButtonSuccess>
                  <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={closeDrawer}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </PageContainer>
  );
}

export default EntityDetails_V2;